import {
  queryOptions,
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { differenceInMinutes } from "date-fns/fp";
import { format } from "date-fns";
import {
  getFolioNumbers,
  getTransactionOrdersApi,
  submitTransactionOrdersApi,
  deleteAllTransactionOrder,
  getHoldingFoliosApi,
  draftOrdersNotifyClientApi,
  generateOrderVerificationOtpApi,
  generateOtpForTransactionPlanCancel,
  cancelTransactionApi,
  refetchTransactionStatusApi,
  fetchTransactions,
  payOrdersApi,
  getPaymentStatusApi,
  getPaymentsListApi,
  generatePaymentLink,
  getEstimatedNextInstallmentDateApi,
  EstimateNextInstallmentPayload,
  EstimatedNextInstallmentDateResponse,
  getLastUsedFolioNumbers,
} from "../../api/transaction/transaction";
import { CustomQueryOptions } from "../../types";
import { useSubmittedOrders } from "../client";
import { planKeys } from "../client/plan";
import { useCreateOneTimeTransactionV2 } from "./one-time";
import { useInvalidateTransactionOrder } from "./invalidate";
import { pauseResumeTransactionApi } from "../../api/transaction";
import { NON_GIFT_PRODUCTS } from "../../constants";

export const useEstimatedNextInstallmentDate = (
  payload: EstimateNextInstallmentPayload | undefined,
  options?: { enabled?: boolean }
) => {
  const enabled = !!payload && (options?.enabled ?? true);

  return useQuery<EstimatedNextInstallmentDateResponse | null>({
    queryKey: ["transaction/next-installment-date", payload],
    queryFn: (): Promise<EstimatedNextInstallmentDateResponse | null> =>
      payload
        ? getEstimatedNextInstallmentDateApi(payload)
        : Promise.resolve(null),
    enabled,
    placeholderData: (prev) => prev,
  });
};

export type EstimatedNextInstallmentDateItem<TKey extends string | number> = {
  key: TKey;
  payload: EstimateNextInstallmentPayload;
};

/**
 * Fetches the estimated next installment date for multiple plans at once.
 * Each item is keyed (typically by plan id) so callers can look up the
 * resolved date per plan. Queries share the same cache key as the single
 * `useEstimatedNextInstallmentDate` hook, so results are deduped/reused.
 */
export const useEstimatedNextInstallmentDates = <TKey extends string | number>(
  items: EstimatedNextInstallmentDateItem<TKey>[],
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled ?? true;

  return useQueries({
    queries: items.map((item) => ({
      queryKey: ["transaction/next-installment-date", item.payload],
      queryFn: (): Promise<EstimatedNextInstallmentDateResponse | null> =>
        getEstimatedNextInstallmentDateApi(item.payload),
      enabled,
      placeholderData: (prev: EstimatedNextInstallmentDateResponse | null) =>
        prev,
    })),
    combine: (results) => {
      const dates = {} as Record<TKey, string | undefined>;
      items.forEach((item, index) => {
        dates[item.key] = results[index]?.data?.installment_date;
      });
      return {
        dates,
        isLoading: results.some((result) => result.isLoading),
        isFetching: results.some((result) => result.isFetching),
        isError: results.some((result) => result.isError),
      };
    },
  });
};

export { useInvalidateTransactionOrder } from "./invalidate";

type getFolioNumbersVariables = {
  investment_account_guid: string;
  giftOnly?: boolean;
};

export const folioNumbersQueryOptions = (variables: getFolioNumbersVariables) =>
  queryOptions({
    queryKey: [`transaction/folio-numbers`, variables],
    queryFn: () =>
      getFolioNumbers(variables.investment_account_guid, variables.giftOnly),
  });

export const useGetFolioNumbers = (variables: getFolioNumbersVariables) => {
  return useQuery(folioNumbersQueryOptions(variables));
};

export const useLastUsedFolios = (variables: getFolioNumbersVariables) => {
  return useQuery({
    queryKey: [`transaction/folio-numbers/last-used`, variables],
    queryFn: () => getLastUsedFolioNumbers(variables.investment_account_guid),
  });
};

export const useFoliosWithLastUsedInfo = (
  variables: getFolioNumbersVariables
) => {
  return useQueries({
    queries: [
      folioNumbersQueryOptions(variables),
      {
        queryKey: [`transaction/folio-numbers/last-used`, variables],
        queryFn: () =>
          getLastUsedFolioNumbers(variables.investment_account_guid),
      },
    ],
    combine: ([foliosQuery, lastUsedQuery]) => {
      const lastUsedIds = new Set(lastUsedQuery.data?.map((f) => f.id) ?? []);
      const data = foliosQuery.data?.map((folio) => ({
        ...folio,
        lastUsed: lastUsedIds.has(folio.id),
      }));
      return {
        data,
        isLoading: foliosQuery.isLoading || lastUsedQuery.isLoading,
        isError: foliosQuery.isError || lastUsedQuery.isError,
      };
    },
  });
};

export const useInfiniteTransactionOrders = (
  investmentAccountId: string,
  enabled = true
) => {
  const ordersResponse = useInfiniteQuery({
    queryKey: [`transaction/orders/get`, { investmentAccountId }],
    queryFn: (context) => {
      return getTransactionOrdersApi(investmentAccountId, context.pageParam);
    },
    getNextPageParam: (lastPage) => {
      return lastPage.orders?.next
        ? lastPage.orders.next?.match(/page=(\d+)/)?.[1]
        : undefined;
    },
    initialPageParam: "1",
    select: (queryResponse) => queryResponse.pages,
    placeholderData: (prev) => prev,
    enabled,
  });

  const orders = useMemo(() => {
    return ordersResponse.data?.flatMap((d) => d?.orders?.results) ?? [];
  }, [ordersResponse.data]);

  return {
    ...ordersResponse,
    orders,
    total: ordersResponse.data?.[0].total_purchase_amount,
  };
};

export const draftTransactionOrdersQueryOptions = (
  investmentAccountId: string,
  enabled: boolean = true
) =>
  queryOptions({
    queryKey: ["transaction/orders/draft", { investmentAccountId }],
    queryFn: () => getTransactionOrdersApi(investmentAccountId, "1", 100),
    enabled,
  });

export const useDraftTransactionOrders = (
  investmentAccountId: string,
  enabled?: boolean
) => {
  return useQuery(
    draftTransactionOrdersQueryOptions(investmentAccountId, enabled)
  );
};

export const useSubmitTransactionOrders = (investmentAccountId: string) => {
  return useMutation({
    mutationKey: ["transaction/orders/submit"],
    mutationFn: ({ orderIds, otp }: { orderIds: number[]; otp: string }) =>
      submitTransactionOrdersApi({
        order_ids: orderIds,
        investment_account_guid: investmentAccountId,
        otp,
      }),
  });
};

export const useDraftOrdersNotifyClient = (investmentAccountId: string) => {
  return useMutation({
    mutationKey: ["transaction/orders/notify-client"],
    mutationFn: () => draftOrdersNotifyClientApi(investmentAccountId),
  });
};

export const useGenerateOrderVerificationOtpMutation = (
  investmentAccountId: string
) => {
  return useMutation({
    mutationKey: ["transaction/orders/generate-otp"],
    mutationFn: () => generateOrderVerificationOtpApi(investmentAccountId),
  });
};

export const useDeleteAllTransactionOrder = (investmentAccountId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [`transaction/orders/delete-all`, { investmentAccountId }],
    mutationFn: () =>
      deleteAllTransactionOrder({
        investment_account_guid: investmentAccountId,
        delete_all: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`transaction/orders/get`, { investmentAccountId }],
      });
    },
  });
};

export const useDeleteTransactionOrder = (investmentAccountId: string) => {
  const { invalidate } = useInvalidateTransactionOrder(investmentAccountId);

  return useMutation({
    mutationKey: [`transaction/orders/delete`, { investmentAccountId }],
    mutationFn: (id: number) =>
      deleteAllTransactionOrder({
        investment_account_guid: investmentAccountId,
        order_ids: [id],
      }),
    onSuccess: invalidate,
  });
};

export const useGetHoldingFolios = (
  investmentAccountId: string,
  isin: string,
  enabled: boolean
) => {
  return useQuery({
    queryKey: [`holdings/folios/${investmentAccountId}/${isin}`],
    queryFn: () => getHoldingFoliosApi(investmentAccountId, isin),
    enabled,
  });
};

export const useGenerateOtpForTransactionPlanCancelMutation = (
  investmentAccountId: string
) => {
  return useMutation({
    mutationKey: [
      "transaction/orders/generate-otp-for-transaction-plan-cancel",
    ],
    mutationFn: ({
      planId,
      planType,
    }: {
      planId: number;
      planType: "xsip" | "stp" | "swp";
    }) =>
      generateOtpForTransactionPlanCancel({
        investment_account_guid: investmentAccountId,
        plan_id: planId,
        plan_type: planType,
      }),
  });
};

export const useRefetchTransactionStatus = (investmentAccountId: string) => {
  return useMutation({
    mutationKey: ["transaction/orders/refetch-status"],
    mutationFn: () => refetchTransactionStatusApi(investmentAccountId),
  });
};

export const useTransactions = (
  variables: {
    investment_account_guid: string;
    page: number;
    page_size: number;
    isin: string;
    external: boolean;
    folio_id?: number;
  },
  options?: CustomQueryOptions<typeof fetchTransactions>
) => {
  return useQuery({
    queryKey: ["transaction/list/get", variables],
    queryFn: () =>
      fetchTransactions({
        investment_account_guid: variables.investment_account_guid,
        page: variables.page,
        page_size: variables.page_size,
        isin: variables.isin,
        external: variables.external,
        folio_id: variables.folio_id,
      }),
    ...options,
    placeholderData: (prev) => prev,
  });
};

export const useCancelTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["transaction/orders/cancel"],
    mutationFn: cancelTransactionApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["transaction/orders/draft"],
      });
    },
  });
};

export const usePauseResumeTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["transaction/orders/pause"],
    mutationFn: pauseResumeTransactionApi,
    onSuccess: (_data, variables) => {
      const investmentAccountId = variables.payload.investment_account_guid;

      // Pause happens immediately on backend, not through draft orders
      // Invalidate SIP data to reflect updated pause status
      queryClient.invalidateQueries({
        queryKey: planKeys.lists("XSIP", investmentAccountId),
      });

      queryClient.invalidateQueries({
        queryKey: planKeys.infiniteLists("XSIP", investmentAccountId),
      });

      queryClient.invalidateQueries({
        queryKey: ["transaction/orders/draft"],
      });
    },
  });
};

export function usePayment() {
  return useMutation({
    mutationFn: payOrdersApi,
  });
}

const paymentStatusQueryOptions = (
  variables: { id: string | number; investmentAccountGuid: string },
  options?: { enabled?: boolean }
) =>
  queryOptions({
    ...options,
    queryFn: () =>
      getPaymentStatusApi(variables.id, variables.investmentAccountGuid),
    queryKey: ["transaction/orders/payment-status", { id: variables.id }],
    refetchInterval: (query) => {
      if (
        query.state.data?.payment_status === "SUCCESS" ||
        query.state.data?.payment_status === "FAILED" ||
        query.state.data?.payment_status === "REJECTED"
      ) {
        return false;
      }
      return 5000;
    },
  });

export function usePaymentStatus(
  variables: { id: string | number; investmentAccountGuid: string },
  options?: { enabled?: boolean }
) {
  return useQuery(paymentStatusQueryOptions(variables, options));
}

const paymentsListQueryOptions = (
  variables: { investment_account_guid: string; order_ids: number[] },
  options?: { enabled?: boolean }
) =>
  queryOptions({
    ...options,
    queryFn: () =>
      getPaymentsListApi(
        variables.investment_account_guid,
        variables.order_ids
      ),
    queryKey: [
      "transaction/payments-list",
      {
        investment_account_guid: variables.investment_account_guid,
        order_ids: variables.order_ids,
      },
    ],
  });

export function usePaymentsList(
  variables: { investment_account_guid: string; order_ids: number[] },
  options?: { enabled?: boolean }
) {
  return useQuery(paymentsListQueryOptions(variables, options));
}

export function usePaymentLink(investmentAccountGuid: string) {
  return useMutation({
    mutationFn: (sendLink: boolean) =>
      generatePaymentLink(investmentAccountGuid, sendLink),
  });
}

export function useRecentlyFailedOrders(investmentAccountId: string) {
  const { orders, isLoading: isLoadingDraftOrders } =
    useInfiniteTransactionOrders(investmentAccountId, !!investmentAccountId);

  const today = useMemo(() => format(new Date(), "dd/MM/yyyy"), []);

  const weekAgo = useMemo(() => {
    const today = new Date();
    return format(today.setDate(today.getDate() - 7), "dd/MM/yyyy");
  }, []);

  const { data } = useSubmittedOrders(
    {
      investment_account_guid: investmentAccountId,
      page: 1,
      pageSize: 50,
      from_date: weekAgo,
      to_date: today,
      productType: NON_GIFT_PRODUCTS.join(","),
    },
    {
      enabled:
        !!investmentAccountId && !isLoadingDraftOrders && orders.length === 0,
    }
  );

  const ordersSubmittedInLastWeek = data?.results ?? [];

  const failureTimestamp = useMemo(
    () =>
      ordersSubmittedInLastWeek[0]?.created_at
        ? new Date(ordersSubmittedInLastWeek[0]?.created_at)
        : new Date(),
    [ordersSubmittedInLastWeek[0]?.created_at]
  );

  const failedOrdersWithinaMinuteFromfailureTimestamp = useMemo(() => {
    const failedOrders = [];
    for (const order of ordersSubmittedInLastWeek) {
      if (order.order_status !== "Failed") break;
      if (order.type !== "PURCHASE") break;
      if (
        Math.abs(
          differenceInMinutes(failureTimestamp, new Date(order.created_at))
        ) <= 1
      ) {
        failedOrders.push(order);
      } else {
        break;
      }
    }
    return failedOrders;
  }, [failureTimestamp, ordersSubmittedInLastWeek]);

  const { mutateAsync: createOneTimeTransaction } =
    useCreateOneTimeTransactionV2();

  const retry = useCallback(async () => {
    const createOrderOperations = await Promise.allSettled(
      failedOrdersWithinaMinuteFromfailureTimestamp.map((o) => {
        return createOneTimeTransaction({
          investment_account_guid: investmentAccountId,
          folio_id: o.folio_id,
          amount: o.amount,
          scheme_isin: o.scheme_isin,
        });
      })
    );

    const failedOrders = createOrderOperations
      .filter((o) => o.status === "rejected")
      .map((o) => o.reason);

    const successfulOrders = createOrderOperations
      .filter((o) => o.status === "fulfilled")
      .map((o) => o.value);

    return [failedOrders, successfulOrders];
  }, [failedOrdersWithinaMinuteFromfailureTimestamp, investmentAccountId]);

  return useMemo(
    () => [failedOrdersWithinaMinuteFromfailureTimestamp, retry] as const,
    [failedOrdersWithinaMinuteFromfailureTimestamp, retry]
  );
}
